import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Layers, ClipboardCheck, Lightbulb, Loader2, GraduationCap, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScoreRings } from "@/components/learning/ScoreRings";
import { FlashcardDeck } from "@/components/learning/FlashcardDeck";
import { QuizConfig } from "@/components/learning/QuizConfig";
import { QuizRunner } from "@/components/learning/QuizRunner";
import { ReflectionPanel } from "@/components/learning/ReflectionPanel";
import { SummariesPanel } from "@/components/reader/SummariesPanel";
import { docsApi, learningApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function Study() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [doc, setDoc] = useState(null);
    const [scores, setScores] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quizConfig, setQuizConfig] = useState(null);

    const loadScores = useCallback(async () => {
        try { setScores((await learningApi.docScores(id)).scores); } catch (e) {}
    }, [id]);

    useEffect(() => {
        (async () => {
            try {
                const d = await docsApi.get(id);
                setDoc(d);
                await loadScores();
            } catch (e) { toast.error(t("study.loadFailed")); navigate("/library"); }
            finally { setLoading(false); }
        })();
    }, [id, navigate, loadScores, t]);

    if (loading) return <div className="flex min-h-screen items-center justify-center bg-surface-0"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;
    if (!doc) return null;

    return (
        <div className="min-h-screen bg-surface-0">
            <header className="sticky top-0 z-20 border-b border-border bg-surface-0/85 backdrop-blur-md">
                <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
                    <div className="flex min-w-0 items-center gap-3">
                        <Button data-testid="study-back-button" variant="ghost" size="icon" className="text-t2" onClick={() => navigate("/library")}><ArrowLeft className="h-5 w-5" /></Button>
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-[hsl(var(--primary))]"><GraduationCap className="h-3.5 w-3.5" /><span className="mono-label">{t("study.mode")}</span></div>
                            <h1 className="truncate font-sans-display text-sm font-semibold text-t1 sm:text-base">{doc.title}</h1>
                        </div>
                    </div>
                    <Button data-testid="study-exam-button" variant="secondary" size="sm" className="gap-1.5 bg-surface-3/60" onClick={() => navigate(`/exam/${id}`)}><Timer className="h-4 w-4" /> {t("study.exam")}</Button>
                </div>
            </header>

            <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
                {scores && <div className="mb-6 rounded-2xl border border-border bg-surface-1 p-5"><ScoreRings scores={scores} size={80} /></div>}

                <Tabs defaultValue="flashcards">
                    <TabsList className="grid w-full grid-cols-4 bg-surface-2">
                        <TabsTrigger data-testid="study-tab-summary" value="summary" className="gap-1.5 text-xs"><Sparkles className="h-3.5 w-3.5" /> {t("study.tabRecap")}</TabsTrigger>
                        <TabsTrigger data-testid="study-tab-flashcards" value="flashcards" className="gap-1.5 text-xs"><Layers className="h-3.5 w-3.5" /> {t("study.tabFlashcards")}</TabsTrigger>
                        <TabsTrigger data-testid="study-tab-quiz" value="quiz" className="gap-1.5 text-xs"><ClipboardCheck className="h-3.5 w-3.5" /> {t("study.tabQuiz")}</TabsTrigger>
                        <TabsTrigger data-testid="study-tab-reflection" value="reflection" className="gap-1.5 text-xs"><Lightbulb className="h-3.5 w-3.5" /> {t("study.tabReflect")}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="summary" className="mt-5">
                        <div className="overflow-hidden rounded-2xl border border-border bg-surface-1" style={{ height: "68vh" }}><SummariesPanel docId={id} /></div>
                    </TabsContent>
                    <TabsContent value="flashcards" className="mt-5"><FlashcardDeck docId={id} onChanged={loadScores} /></TabsContent>
                    <TabsContent value="quiz" className="mt-5">
                        {quizConfig ? (
                            <QuizRunner docId={id} config={quizConfig} onExit={() => { setQuizConfig(null); loadScores(); }} onScored={loadScores} />
                        ) : (
                            <QuizConfig mode="quiz" onStart={(opts) => setQuizConfig(opts)} />
                        )}
                    </TabsContent>
                    <TabsContent value="reflection" className="mt-5"><ReflectionPanel docId={id} /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
